import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import type { Query } from '@tanstack/react-query';

/**
 * Board-Daten im IndexedDB-Cache, damit die App offline LESBAR bleibt
 * (SPEC 9.7, Akzeptanz A12).
 *
 * Der Service Worker haelt die Huelle vor — das allein reicht nicht: ohne
 * Antwort auf `GET /auth/me` landet `App.tsx` beim Login, und der Cache von
 * React Query lebt nur im Arbeitsspeicher. Im Flugmodus sah man deshalb
 * bisher die Huelle und dann die Anmeldung.
 *
 * Geschrieben wird gegen IndexedDB von Hand, ohne `idb` oder `idb-keyval`:
 * es sind drei Zugriffe auf einen einzigen Schluessel.
 *
 * WAS abgelegt wird, ist eine Sicherheitsfrage, keine Geschmacksfrage — siehe
 * `persistQuery`.
 */

const DB_NAME = 'kanban-offline';
const STORE_NAME = 'abfragen';
const ENTRY_KEY = 'query-client';

/**
 * Wandert mit dem Datenformat. Ein Deploy, der `BoardDto` aendert, darf den
 * alten Stand nicht einspielen — sonst rendert die neue Oberflaeche gegen
 * Felder, die es in der abgelegten Fassung noch nicht gab.
 */
export const CACHE_BUSTER = 'kanban-4.3';

/** Sieben Tage. Danach ist ein Board-Stand keine Hilfe mehr, sondern eine Falle. */
export const CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    /*
     * Ein zweiter Tab kann eine Versionsaenderung blockieren. Dann hier
     * abbrechen statt ewig zu warten — die App laeuft auch ohne Cache, sie
     * ist dann nur nicht offline lesbar.
     */
    request.onblocked = () => reject(new Error('IndexedDB blockiert'));
  });
}

function withStore<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);
        const request = action(transaction.objectStore(STORE_NAME));
        transaction.oncomplete = () => {
          db.close();
          resolve(request.result);
        };
        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
      }),
  );
}

/**
 * Persister fuer React Query.
 *
 * Jeder Fehler wird geschluckt. IndexedDB fehlt im privaten Modus mancher
 * Browser, laesst sich abschalten und laeuft voll — ein Board, das sich
 * deswegen nicht oeffnet, waere der schlechtere Tausch als eines, das offline
 * eben nichts anzuzeigen hat.
 */
export const offlinePersister: Persister = {
  persistClient: async (client: PersistedClient) => {
    try {
      await withStore('readwrite', (store) => store.put(client, ENTRY_KEY));
    } catch {
      // still
    }
  },
  restoreClient: async () => {
    try {
      return await withStore<PersistedClient | undefined>('readonly', (store) =>
        store.get(ENTRY_KEY),
      );
    } catch {
      return undefined;
    }
  },
  removeClient: async () => {
    try {
      await withStore('readwrite', (store) => store.delete(ENTRY_KEY));
    } catch {
      // still
    }
  },
};

/** Den abgelegten Stand verwerfen — bei jedem Nutzerwechsel. */
export async function clearOfflineCache(): Promise<void> {
  await offlinePersister.removeClient();
}

/**
 * Welche Abfragen auf dem Geraet liegen duerfen.
 *
 * Eine Erlaubnisliste, und eine knappe. SPEC 9.7 sagt „Board-Daten im
 * IndexedDB-Cache" — nicht „alles, was je ueber die Leitung ging". Drin sind:
 *
 *   `['session']`   ohne sie landet der Start beim Login, und dann nuetzt der
 *                   Rest nichts;
 *   `['boards']`    die Liste, mit der die App aufmacht;
 *   `['board', id]` der eigentliche Inhalt;
 *   `['users']`     die Namen zu den Avataren auf den Karten.
 *
 * Draussen bleiben Suchtreffer, Kommentare, Verlauf, Archiv, Erinnerungen und
 * die Adresse des Kalender-Abos. Das ist kein Sparzwang: je weniger dauerhaft
 * auf einem Familiengeraet liegt, desto weniger sieht der naechste, der es in
 * die Hand nimmt.
 */
export function persistQuery(query: Query): boolean {
  // Ein Fehlschlag gehoert nicht in den Cache — sonst startet die App beim
  // naechsten Mal in einen Fehler, den es gar nicht mehr gibt.
  if (query.state.status !== 'success') return false;

  const [root, ...rest] = query.queryKey;
  if (root === 'session' || root === 'boards' || root === 'users') return rest.length === 0;
  // Genau `['board', id]`. `['board', id, 'verlauf']` und `['board', id,
  // 'archiv']` sind Nachschlagewerke, keine Board-Daten.
  if (root === 'board') return rest.length === 1;
  return false;
}
